/**
 * Ion Access Service
 *
 * Resolves whether the current user is allowed to use Ion and, if so,
 * what their tier-aware quota and model allowlist look like. Owns the
 * single source of truth for the launcher gate, the `/assistant` route
 * gate, and the kill-switch banner.
 *
 * Flow:
 *   1. `bootstrapChatAccess()` is called once after authentication settles
 *      (typically from `AppShell`). It hits `GET /api/v1/chat/usage/me`.
 *   2. On 200, the user is allowed; we record `isAdmin` and seed the
 *      dual-bucket quota signal so subsequent guards (e.g. IonSurface
 *      visibility) have non-loading state immediately.
 *   3. On 403, we record `reason` based on the backend's `{detail.code}`
 *      envelope so the UI can show a specific explanation. The launcher
 *      hides itself, but the SubscriptionPage redirect is handled by the
 *      route gate, not here.
 *   4. `/chat/models` is fetched lazily on the first explicit call so
 *      surfaces that don't render a model picker don't pay the roundtrip.
 *
 * Pre-flight refresh: a successful `/chat/usage/me` doubles as a token
 * refresh trigger because it goes through `authenticatedFetch`, which
 * handles 401-with-refresh-retry. Calling `bootstrapChatAccess()` right
 * before opening the chat reduces the chance of a stale access token
 * mid-stream.
 */

import { authenticatedFetch } from './authService.js'
import {
  setChatAccess,
  updateQuota,
  chatAccess,
  ionFeatureEnabled,
  insightsTokenBudget,
  weeklyTokenBudget,
  monthlyTokenBudget,
  tierProfile,
} from '../state/chatStore.js'

const USAGE_ME_URL = '/api/v1/chat/usage/me'
const PACING_URL = '/api/v1/chat/usage/me/pacing'
const MODELS_URL = '/api/v1/chat/models'

/**
 * Codes the backend may return in the `detail.code` field of a 403 response.
 * Mirrors `quota.py:ensure_chat_access()`. Anything not in this set falls
 * back to `unknown` so the UI still shows *something* instead of a blank
 * banner.
 */
const KNOWN_REASONS = new Set([
  'free_tier',
  'no_subscription',
  'inactive',
  'disabled_by_admin',
  'kill_switch',
  'quota_exceeded',
])

function normaliseReason(raw) {
  if (typeof raw !== 'string') return 'unknown'
  return KNOWN_REASONS.has(raw) ? raw : 'unknown'
}

/**
 * Parse the FastAPI error envelope. `detail` may be either a string (older
 * endpoints) or an object `{code, message}` (newer ones).
 */
async function parseErrorDetail(response) {
  try {
    const body = await response.json()
    const detail = body?.detail
    if (typeof detail === 'string') return { code: 'unknown', message: detail }
    if (detail && typeof detail === 'object') {
      return {
        code: normaliseReason(detail.code),
        message: detail.message || detail.code || 'Access denied',
      }
    }
  } catch {
    /* non-JSON body — fall through */
  }
  return { code: 'unknown', message: `HTTP ${response.status}` }
}

/**
 * Resolve the current user's Ion access state. Idempotent — calling it
 * again refreshes the state (useful right before opening the chat surface).
 *
 * @returns {Promise<typeof chatAccess.value>}
 */
export async function bootstrapChatAccess() {
  setChatAccess({ loading: true })

  try {
    const response = await authenticatedFetch(USAGE_ME_URL)

    if (response.ok) {
      const data = await response.json().catch(() => ({}))
      // The interceptor in authService already piped the X-Chat-Daily-* /
      // X-Chat-Weekly-* headers into tokenBudget; we additionally seed the
      // signal from the body to cover endpoints that produce body-only
      // (non-header) usage responses.
      updateQuota({
        daily: {
          pct: Number.isFinite(data.dailyUsedPct) ? data.dailyUsedPct : null,
          resetAt: data.dailyResetAt || null,
        },
        weekly: {
          pct: Number.isFinite(data.weeklyUsedPct) ? data.weeklyUsedPct : null,
          resetAt: data.weeklyResetAt || null,
        },
        isAdmin: !!data.isAdmin,
      })
      // Env-level Ion feature flag (chat_default_enabled) — independent of
      // per-user access. Admins bypass the 403 gate above but still need
      // this to know Ion is off for the whole environment (Phase-3 prep).
      ionFeatureEnabled.value =
        typeof data.ionFeatureEnabled === 'boolean' ? data.ionFeatureEnabled : null
      // PRO/DEMO "Insights Token" meter — cost-derived display unit
      // ($0.25/token, see backend chat/token_units.py). Values are decimal
      // STRINGS (fractional tokens are valid) — null limit = unlimited.
      // (2026-07-17: the DEMO-only request-COUNT meter was removed — DEMO
      // now reads this same token meter, same as PRO/batemo.)
      insightsTokenBudget.value = {
        used: typeof data.dailyTokensUsed === 'string' ? data.dailyTokensUsed : null,
        limit: typeof data.dailyTokensLimit === 'string' ? data.dailyTokensLimit : null,
        warning: !!data.dailyWarning,
      }
      // Weekly Insights Token budget (parity fix, 2026-07-17) — same
      // token-display convention, the window between daily and monthly.
      weeklyTokenBudget.value = {
        used: typeof data.weeklyTokensUsed === 'string' ? data.weeklyTokensUsed : null,
        limit: typeof data.weeklyTokensLimit === 'string' ? data.weeklyTokensLimit : null,
        warning: !!data.weeklyWarning,
      }
      // Monthly Insights Token budget (Ion AI limits system, 2026-07) — same
      // token-display convention, one level up the window hierarchy.
      // resetAt is the literal string "never" for DEMO.
      monthlyTokenBudget.value = {
        used: typeof data.monthlyTokensUsed === 'string' ? data.monthlyTokensUsed : null,
        limit: typeof data.monthlyTokensLimit === 'string' ? data.monthlyTokensLimit : null,
        resetAt: data.monthlyResetAt || null,
        warning: !!data.monthlyWarning,
      }
      tierProfile.value = typeof data.tierProfile === 'string' ? data.tierProfile : null
      setChatAccess({
        loading: false,
        available: true,
        isAdmin: !!data.isAdmin,
        reason: null,
      })
      return chatAccess.value
    }

    if (response.status === 403) {
      const { code } = await parseErrorDetail(response)
      setChatAccess({
        loading: false,
        available: false,
        isAdmin: false,
        reason: code,
      })
      return chatAccess.value
    }

    if (response.status === 401) {
      // authenticatedFetch already attempted a refresh; if we still get 401,
      // the user isn't logged in. Treat as "not available" without a reason —
      // the route gate will handle redirect to /login.
      setChatAccess({
        loading: false,
        available: false,
        isAdmin: false,
        reason: 'unauthenticated',
      })
      return chatAccess.value
    }

    // 5xx or unexpected — keep showing the launcher (treat as available) but
    // flag the reason. We'd rather let the user attempt a chat and surface a
    // real error than hide the entrypoint based on a transient outage.
    setChatAccess({
      loading: false,
      available: true,
      isAdmin: false,
      reason: 'network',
    })
    return chatAccess.value
  } catch (err) {
    console.warn('[ionAccessService] bootstrap failed:', err)
    setChatAccess({
      loading: false,
      available: true,
      isAdmin: false,
      reason: 'network',
    })
    return chatAccess.value
  }
}

/**
 * Lazy-load the model allowlist. Cached via the chatAccess signal so a
 * second caller doesn't refetch.
 */
export async function loadAllowedModels() {
  const current = chatAccess.peek()
  if (current.allowedModels && current.allowedModels.length > 0) {
    return { models: current.allowedModels, default: current.defaultModel }
  }

  try {
    const response = await authenticatedFetch(MODELS_URL)
    if (!response.ok) return { models: [], default: null }
    const data = await response.json().catch(() => ({}))
    const models = Array.isArray(data.models) ? data.models : []
    const defaultModel = data.default || null
    setChatAccess({ allowedModels: models, defaultModel })
    return { models, default: defaultModel }
  } catch (err) {
    console.warn('[ionAccessService] loadAllowedModels failed:', err)
    return { models: [], default: null }
  }
}

/**
 * Self-service daily/weekly pacing (PRO/batemo only — Ion AI limits system,
 * 2026-07). `dailyLimitUsd`/`weeklyLimitUsd` are USD Decimal-strings or
 * `null` (null = no cap for that window, up to the monthly ceiling).
 * Monthly is never settable here — the backend endpoint has no such field.
 *
 * On success, refreshes `bootstrapChatAccess()` so the usage meters reflect
 * the new limits immediately (avoids a stale daily/weekly display until the
 * next natural /chat/usage/me poll).
 *
 * @returns {Promise<{ok: boolean, error?: {code: string, [key: string]: any}}>}
 */
export async function setPacingLimits({ dailyLimitUsd = null, weeklyLimitUsd = null } = {}) {
  try {
    const response = await authenticatedFetch(PACING_URL, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dailyLimitUsd, weeklyLimitUsd }),
    })

    if (response.ok) {
      await bootstrapChatAccess()
      return { ok: true }
    }

    const body = await response.json().catch(() => ({}))
    const detail = body?.detail
    if (detail && typeof detail === 'object') {
      return { ok: false, error: detail }
    }
    return { ok: false, error: { code: 'unknown', message: `HTTP ${response.status}` } }
  } catch (err) {
    console.warn('[ionAccessService] setPacingLimits failed:', err)
    return { ok: false, error: { code: 'network' } }
  }
}

/**
 * Apply a 403 response received during a streaming attempt to the access
 * state. The dispatch path in `useChatStream.sendChatMessage` calls this
 * when it sees an HTTP 403 so the launcher hides itself for subsequent
 * page interactions without forcing a full page reload.
 */
export function recordAccessDenial(code) {
  setChatAccess({
    loading: false,
    available: false,
    reason: normaliseReason(code),
  })
}
